import React, { HTMLProps, ReactNode, forwardRef, useEffect, useRef } from 'react'
import { Button } from 'primereact/button'
import { Chip } from 'primereact/chip'

import Chevron from '../../components/Chevron'
import { Input } from '../../uikit/Input'
import { DropdownMenu } from '../../components/Menu'
import { ScreenDeviceType } from '../../enums/screenDeviceTypeEnum'
import useMediaQueryHook from '../../hooks/useMediaQueryHook'
import { useIsComponentVisible } from '../../hooks/useIsComponentVisible'
import { CloseIcon } from '../../icons/close-icon/CloseIcon'
import FilterIcon from '../../../lib/icons/filter-icon/filter-icon'
import { theme } from '../../../lib/theme'
import { KeyEvents } from '../../constants/KeyEventContants'
import useText from '../../hooks/useText'

import type { DecoratedFilterValue, FilterValue } from './charge-points-search-widget'
import './search-input.css'

export type SearchInputProps = {
    onSubmit: () => void
    onClear: (filterName?: string) => void
    filterDropdownItems?: ReactNode
    activeFilters?: { key: string; value: string | number | boolean; label?: string; labelValue?: string | null }[]
} & Omit<HTMLProps<HTMLInputElement>, 'onSubmit' | 'ref'>

type FilterDropdownProps = {
    isOpen: boolean
    close: () => void
} & HTMLProps<HTMLElement>

const FilterDropdown = forwardRef<HTMLUListElement, FilterDropdownProps>(({ children, isOpen }, ref) => {
    return (
        isOpen && (
            <DropdownMenu
                ref={ref}
                className="absolute right-0 xs:left-0 z-1 flex flex-column gap-4 z-2 text-sm w-full md:w-30rem"
                style={{ top: '50px' }}
                data-testid="search-input-filter-dropdown"
                tabIndex={-1}
            >
                {children}
            </DropdownMenu>
        )
    )
})

type ActiveFilterButtonProps = {
    label: string
    clearFilter: () => void
}

const ActiveFilterButton = ({ label, clearFilter }: ActiveFilterButtonProps) => {
    return (
        <button
            type="button"
            onClick={() => clearFilter()}
            className="flex align-items-center justify-content-center gap-1 border-none bg-transparent hover:surface-200 cursor-pointer font-bold text-base p-4 pr-5 text-500 ml-auto"
        >
            <FilterIcon size={16} fill={theme.font.textColor} />
            {label}
            <CloseIcon size={16} className="ml-2 justify-self-end" />
        </button>
    )
}

type ActiveFilterChipsProps = {
    activeFilters: DecoratedFilterValue[]
    clearFilter: (filterName: string) => void
} & HTMLProps<HTMLDivElement>

const ActiveFilterChips = ({ activeFilters, clearFilter }: ActiveFilterChipsProps) => {
    const text = useText()
    return (
        <div className="flex align-items-start py-3 pl-4 gap-2">
            <div className="flex align-items-center gap-2 flex-nowrap min-w-max text-500 mr-4">
                <FilterIcon size={16} />
                <div>{text('lblActiveFiltersHeading')}</div>
            </div>
            <div className="flex flex-wrap justify-content-end align-items-center gap-2">
                {activeFilters.map((filter) => {
                    const label = `${filter.label ?? filter.key}${filter.labelValue ? ':' : ''} ${
                        filter.labelValue ?? filter.value
                    }`
                    return (
                        <Chip
                            key={filter.key}
                            label={label}
                            className="gap-1 border-1 border-300 surface-100 text-500 text-xs font-bold line-height-2 py-1 px-2"
                            template={
                                <>
                                    <span>
                                        {filter.label ?? filter.key}
                                        {filter.labelValue && ':'}
                                    </span>
                                    <span className="text-gray-800 font-bold">
                                        {String(filter.labelValue ?? filter.value)}
                                    </span>
                                    <button
                                        type="button"
                                        aria-label={text('btnRemoveActiveFilter', { filter: label })}
                                        className="p-chip-remove-icon flex align-items-center justify-content-center border-none bg-transparent text-500 p-0 m-0 cursor-pointer"
                                        onClick={() => clearFilter(filter.key)}
                                        onKeyDown={(event) => {
                                            if (event.key === KeyEvents.ENTER || event.key === 'Backspace') {
                                                event.preventDefault()
                                                event.stopPropagation()
                                                clearFilter(filter.key)
                                            }
                                        }}
                                    >
                                        <CloseIcon size={14} />
                                    </button>
                                </>
                            }
                            data-testid={`active-filter-${filter.key}`}
                        />
                    )
                })}
            </div>
        </div>
    )
}

type ActiveFilterContainerProps = {
    activeFilters: FilterValue[]
    clear: (filterName?: string) => void
}

const ActiveFilterContainer = ({ activeFilters, clear }: ActiveFilterContainerProps) => {
    const text = useText()
    const deviceType = useMediaQueryHook()

    return deviceType === ScreenDeviceType.Mobile ? (
        <ActiveFilterButton
            label={text('lblActiveFilters', { activeFilters: activeFilters.length })}
            clearFilter={clear}
        />
    ) : (
        <ActiveFilterChips activeFilters={activeFilters} clearFilter={clear} />
    )
}

const SearchWithFilter = forwardRef<HTMLInputElement, SearchInputProps>(
    ({ onSubmit, onClear, activeFilters, filterDropdownItems, ...rest }, ref): React.ReactElement => {
        const text = useText()
        const buttonContainerRef = useRef<HTMLDivElement>(null)
        const { isComponentVisible, setIsComponentVisible } = useIsComponentVisible(false, buttonContainerRef)
        useEffect(() => {
            const keyDownListener = (event: KeyboardEvent) => {
                if (event.key === KeyEvents.ESCAPE) {
                    setIsComponentVisible(false)
                }
            }
            const keypressListener = (event: KeyboardEvent) => {
                if (event.key === KeyEvents.ENTER) {
                    onSubmit()
                }
            }

            window.addEventListener('keydown', keyDownListener)
            window.addEventListener('keypress', keypressListener)
            return () => {
                window.removeEventListener('keydown', keyDownListener)
                window.removeEventListener('keypress', keypressListener)
            }
        }, [onSubmit, setIsComponentVisible])

        const toggleFilterMenu = (shouldOpenFilterMenu: boolean) => {
            if (!isComponentVisible && activeFilters && activeFilters.length === 0) {
                onClear()
            }
            setIsComponentVisible(shouldOpenFilterMenu)
        }

        return (
            <form className="w-full relative" onSubmit={onSubmit}>
                <div className="gap-4 sm:gap-0 flex flex-row flex-wrap sm:flex-nowrap">
                    <Input
                        ref={ref}
                        icon="pi pi-search"
                        type="search"
                        className="w-full sm:flex-1 min-w-0"
                        inputClassName="h-3rem pr-4 sm:pr-7 border-round-3xl"
                        {...rest}
                        id={rest.id ?? 'search-input'}
                    />
                    <div
                        className="charge-points-search-actions relative flex w-full sm:w-auto h-3rem flex-shrink-0 sm:-ml-6"
                        ref={buttonContainerRef}
                    >
                        <Button
                            type="button"
                            className="charge-points-search-button border-round-left-3xl border-noround-right text-base flex-1 px-5 py-2"
                            label={text('lblSearch')}
                            onClick={() => {
                                onSubmit()
                                toggleFilterMenu(false)
                            }}
                        />
                        {filterDropdownItems && (
                            <Button
                                type="button"
                                data-testid="filter-dropdown-btn"
                                icon={
                                    <Chevron
                                        flipped={!isComponentVisible}
                                        color={
                                            activeFilters && activeFilters.length > 0
                                                ? theme.brandColors.CA_Green
                                                : theme.brandColors.CA_White
                                        }
                                        size={20}
                                    />
                                }
                                aria-label={text('lblFilterModal')}
                                className="charge-points-filter-button border-round-right-3xl border-noround-left text-base w-3rem flex-shrink-0 p-2 filter-dropdown-btn"
                                onClick={() => {
                                    setIsComponentVisible(!isComponentVisible)
                                }}
                            />
                        )}
                    </div>
                </div>
                <FilterDropdown isOpen={isComponentVisible} close={() => setIsComponentVisible(false)}>
                    {filterDropdownItems}
                    <div className="flex gap-2">
                        <Button
                            type="button"
                            label={text('btnClear')}
                            icon="pi pi-times"
                            outlined
                            className="flex-1"
                            onClick={() => {
                                onClear()
                                toggleFilterMenu(false)
                            }}
                        />
                        <Button
                            type="button"
                            label={text('btnApply')}
                            icon="pi pi-check"
                            className="flex-1"
                            onClick={() => {
                                onSubmit()
                                toggleFilterMenu(false)
                            }}
                        />
                    </div>
                </FilterDropdown>

                {activeFilters && activeFilters.length > 0 && (
                    <ActiveFilterContainer
                        activeFilters={activeFilters}
                        clear={(filterName?: string) => {
                            onClear(filterName)
                            toggleFilterMenu(false)
                        }}
                    />
                )}
            </form>
        )
    }
)

export default SearchWithFilter
